/** LatestFinancialGrid: responsive Swiper carousel for financial result cards */
"use client";

import React, { useCallback, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Scrollbar } from "swiper/modules";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import type { FinancialNewsItem } from "../../interfaces/LatestFinancialResult.interface";
import LatestFinancialCard from "./LatestFinancialCard";
import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";

interface LatestFinancialGridProps {
  stocks: FinancialNewsItem[];
  sectionTitle?: string;
  componentPosition?: string;
}

const MOBILE_MEDIA_QUERY = "(max-width: 480px)";
const DESKTOP_SWIPER_CONFIG = {
  direction: "horizontal" as const,
  spaceBetween: 0,
  slidesPerView: "auto" as const,
  allowTouchMove: false,
  observer: true,
  observeParents: true,
  modules: [Navigation, Scrollbar, A11y],
  scrollbar: { draggable: false },
};

const LatestFinancialGrid: React.FC<LatestFinancialGridProps> = ({
  stocks,
  sectionTitle,
  componentPosition,
}) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const trackSwiperIconClick = useCallback(
    (ctaText: "Next" | "Previous") => {
      void (async () => {
        try {
          const customerId = await getCustomerIdForAnalytics();
          await trackComponent({
            componentName: "ipo_card_sku_descriptive",
            sectionTitle: sectionTitle ?? "Latest financial results",
            eventType: dlEventConstants.CLICK,
            clickType: "ipo_card_sku_descriptive_icon_click",
            ctaText,
            productCategory: "investment",
            cleverTapOverrides: {
              EP_REFERENCE_ID: "latest_financial_results",
              ...(customerId && { EP_CUSTOMER_ID: customerId }),
              ...(componentPosition && { EP_SR_NUMBER: componentPosition }),
              EP_ENTRYPOINT: "horizontal/entrypoint",
            },
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to track latest financial swiper icon click", error);
        }
      })();
    },
    [componentPosition, sectionTitle],
  );

  const trackCardClick = useCallback(() => {
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        await trackComponent({
          componentName: "ipo_card_sku_descriptive",
          sectionTitle: sectionTitle ?? "Latest financial results",
          eventType: dlEventConstants.CLICK,
          clickType: "ipo_card_sku_descriptive_card_click",
          ctaText: dlEventConstants.FINANCIALS_CARD_CLICK,
          productCategory: "investment",
          cleverTapOverrides: {
            EP_REFERENCE_ID: "latest_financial_results",
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            ...(componentPosition && { EP_SR_NUMBER: componentPosition }),
            EP_ENTRYPOINT: "horizontal/entrypoint",
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track latest financial card click", error);
      }
    })();
  }, [componentPosition, sectionTitle]);

  const handleBeforeInit = useCallback((swiper: SwiperType) => {
    if (typeof swiper.params.navigation !== "boolean" && swiper.params.navigation) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
  }, []);

  const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper;
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  }, []);

  if (!stocks?.length) {
    return null;
  }

  return (
    <div className="relative ">
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {stocks.map((stock, index) => {
            const primaryCompany = stock.companies?.[0];
            return (
              <LatestFinancialCard
                key={stock.id ?? `latest-financial-mobile-${index}`}
                id={stock.id}
                logo={stock.banner_link ?? ""}
                fallbackImage="https://cms-assets.bajajfinserv.in/is/image/bajajfinancestage/stocksplaceholder"
                heading={stock.title}
                symbol={primaryCompany?.symbol ?? ""}
                perChange={primaryCompany?.percentage ?? 0}
                createdAt={stock.created_at ?? ""}
                componentPosition={(index + 1).toString()}
                onCardClick={trackCardClick}
              />
            );
          })}
        </div>
      ) : (
        <>
          <SwiperButton
            ref={prevRef}
            direction="prev"
            isDisabled={isAtStart}
            aria-label="Previous financial result"
            onClick={() => trackSwiperIconClick("Previous")}
          />
          <SwiperButton
            ref={nextRef}
            direction="next"
            isDisabled={isAtEnd}
            aria-label="Next financial result"
            onClick={() => trackSwiperIconClick("Next")}
          />

          <Swiper
            {...DESKTOP_SWIPER_CONFIG}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={handleBeforeInit}
            onInit={handleSwiperUpdate}
            onSlideChange={handleSwiperUpdate}
            onReachEnd={() => setIsAtEnd(true)}
            onFromEdge={() => setIsAtEnd(false)}
          >
            {stocks.map((stock, index) => {
              const primaryCompany = stock.companies?.[0];
              return (
                <SwiperSlide
                  key={stock.id ?? `latest-financial-desktop-${index}`}
                  className={`min-w-[455.33px]! max-w-[455.33px] ${
                    index === stocks.length - 1 ? "mr-0!" : "mr-3!"
                  }`}
                >
                  <LatestFinancialCard
                    id={stock.id}
                    logo={stock.banner_link ?? ""}
                    fallbackImage="https://cms-assets.bajajfinserv.in/is/image/bajajfinancestage/stocksplaceholder"
                    heading={stock.title}
                    symbol={primaryCompany?.symbol ?? ""}
                    perChange={primaryCompany?.percentage ?? 0}
                    createdAt={stock.created_at ?? ""}
                    componentPosition={(index + 1).toString()}
                    onCardClick={trackCardClick}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </>
      )}
    </div>
  );
};

export default LatestFinancialGrid;
